import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type { PlatformDashboard } from '../../api/m19';
import { Panel } from '../../components/ui';
import { count, plural } from '../../lib/format';
import { conversionRate, monthLabel, planSlices, shortDate } from './lib';

type Props = { data?: PlatformDashboard; loading: boolean };

const axisText = { fontSize: 11 };
/** Recessive grid and axis ink shared by every chart here. */
const chartSx = {
  '& .MuiChartsGrid-line': { strokeDasharray: '4 4', opacity: 0.5 },
  '& .MuiChartsAxis-tickLabel': { fill: 'var(--mui-palette-text-secondary)' },
  '& .MuiChartsLegend-label': { color: 'var(--mui-palette-text-secondary)' },
} as const;
const yCount = {
  width: 36,
  tickMinStep: 1,
  valueFormatter: (v: number) => count(v),
  tickLabelStyle: axisText,
  disableLine: true,
  disableTicks: true,
};
const bandX = (data: string[], format: (v: string) => string) => ({
  scaleType: 'band' as const,
  data,
  valueFormatter: format,
  categoryGapRatio: 0.35,
  barGapRatio: 0.15,
  tickLabelStyle: axisText,
  disableLine: true,
  disableTicks: true,
});

function Loading({ height = 240 }: { height?: number }) {
  return <Skeleton variant="rounded" height={height} />;
}

/** Running total of shops, last 12 months. */
export function GrowthPanel({ data, loading }: Props) {
  const rows = data?.growth ?? [];
  const first = rows[0]?.totalShops ?? 0;
  const last = rows.at(-1)?.totalShops ?? 0;
  return (
    <Panel
      title="Platform growth"
      subtitle={
        data
          ? `${plural(last, 'shop')} now · ${last >= first ? '+' : ''}${count(last - first)} in 12 months`
          : 'Shops on the platform, last 12 months'
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ mx: -1 }} data-testid="growth-chart">
          <LineChart
            height={240}
            margin={{ left: 0, right: 28, top: 12, bottom: 0 }}
            grid={{ horizontal: true }}
            hideLegend
            xAxis={[
              {
                scaleType: 'point',
                data: rows.map((r) => r.month),
                valueFormatter: (v: string) => monthLabel(v),
                tickLabelStyle: axisText,
                disableLine: true,
                disableTicks: true,
              },
            ]}
            yAxis={[yCount]}
            series={[
              {
                id: 'total',
                label: 'Shops',
                data: rows.map((r) => r.totalShops),
                area: true,
                showMark: false,
                curve: 'monotoneX',
                color: 'var(--mui-palette-primary-main)',
                valueFormatter: (v: number | null, ctx) => {
                  const added = rows[ctx.dataIndex]?.newShops ?? 0;
                  return `${plural(v ?? 0, 'shop')} (+${count(added)} that month)`;
                },
              },
            ]}
            sx={{
              ...chartSx,
              '& .MuiAreaElement-root': { fillOpacity: 0.14 },
              '& .MuiLineElement-root': { strokeWidth: 2 },
            }}
          />
        </Box>
      )}
    </Panel>
  );
}

/** Shops created vs shops that paid for the first time, per month. */
export function ConversionPanel({ data, loading }: Props) {
  const rows = data?.conversions ?? [];
  const rate = conversionRate(rows);
  return (
    <Panel
      title="Trial to paid"
      subtitle={
        rate === null
          ? 'New shops and first payments, last 6 months'
          : `${String(rate)}% of new shops paid, last 6 months`
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ mx: -1 }} data-testid="conversion-chart">
          <BarChart
            height={240}
            margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
            grid={{ horizontal: true }}
            borderRadius={4}
            slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
            xAxis={[
              bandX(
                rows.map((r) => r.month),
                monthLabel,
              ),
            ]}
            yAxis={[yCount]}
            series={[
              {
                id: 'trials',
                label: 'New shops (trial)',
                data: rows.map((r) => r.trialsStarted),
                color: 'var(--viz-1)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'new shop'),
              },
              {
                id: 'paid',
                label: 'First payments',
                data: rows.map((r) => r.converted),
                color: 'var(--viz-2)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'shop'),
              },
            ]}
            sx={chartSx}
          />
        </Box>
      )}
    </Panel>
  );
}

/** Paying shops by plan, as a donut with the total in the middle. */
export function PlanMixPanel({ data, loading }: Props) {
  const slices = planSlices(data?.planMix ?? []);
  const total = slices.reduce((n, s) => n + s.value, 0);
  return (
    <Panel title="Plans" subtitle="Paying shops by current plan">
      {loading ? (
        <Loading />
      ) : total === 0 ? (
        <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
          No paying shops yet.
        </Typography>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }}
          spacing={2}
          sx={{ alignItems: 'center' }}
          data-testid="plan-chart"
        >
          <Box sx={{ position: 'relative', width: 200, height: 200, flexShrink: 0 }}>
            <PieChart
              width={200}
              height={200}
              margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
              hideLegend
              series={[
                {
                  data: slices.map((s) => ({
                    id: s.id,
                    value: s.value,
                    label: s.label,
                    color: s.color,
                  })),
                  innerRadius: 62,
                  outerRadius: 96,
                  paddingAngle: 1.5,
                  cornerRadius: 4,
                  valueFormatter: (v) =>
                    `${plural(v.value, 'shop')} · ${String(Math.round((v.value / total) * 100))}%`,
                },
              ]}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                placeItems: 'center',
                pointerEvents: 'none',
                textAlign: 'center',
              }}
            >
              <Box>
                <Typography variant="h4" className="tabular" sx={{ lineHeight: 1 }}>
                  {count(total)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  paying
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack
            spacing={1}
            sx={{ flex: 1, minWidth: 0, width: '100%' }}
            component="ul"
            aria-label="Plans"
          >
            {slices.map((s) => (
              <Stack
                key={s.id}
                component="li"
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', listStyle: 'none' }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '3px',
                    bgcolor: s.color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {s.label}
                </Typography>
                <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
                  {count(s.value)}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  className="tabular"
                  sx={{ width: 36, textAlign: 'right' }}
                >
                  {String(Math.round((s.value / total) * 100))}%
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Panel>
  );
}

/** Paid periods ending in each of the next 8 weeks, paying vs trial shops stacked. */
export function RenewalsPanel({ data, loading }: Props) {
  const rows = data?.renewalsDue ?? [];
  const paid = rows.reduce((n, r) => n + r.paid, 0);
  const trial = rows.reduce((n, r) => n + r.trial, 0);
  return (
    <Panel
      title="Renewals due"
      subtitle={
        data
          ? `${plural(paid, 'paying shop')} and ${plural(trial, 'trial')} end in the next 8 weeks`
          : 'Next 8 weeks'
      }
    >
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ mx: -1 }} data-testid="renewals-chart">
          <BarChart
            height={240}
            margin={{ left: 0, right: 12, top: 12, bottom: 0 }}
            grid={{ horizontal: true }}
            borderRadius={4}
            slotProps={{ legend: { position: { vertical: 'top', horizontal: 'end' } } }}
            xAxis={[
              bandX(
                rows.map((r) => r.weekStart),
                (v) => `w/c ${shortDate(v)}`,
              ),
            ]}
            yAxis={[yCount]}
            series={[
              {
                id: 'paid',
                label: 'Paying shops',
                data: rows.map((r) => r.paid),
                stack: 'due',
                color: 'var(--viz-1)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'shop'),
              },
              {
                id: 'trial',
                label: 'Trials ending',
                data: rows.map((r) => r.trial),
                stack: 'due',
                color: 'var(--viz-2)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'shop'),
              },
            ]}
            sx={{
              ...chartSx,
              // 2px surface gap between stacked segments.
              '& .MuiBarElement-root': {
                stroke: 'var(--mui-palette-background-paper)',
                strokeWidth: 2,
              },
            }}
          />
        </Box>
      )}
    </Panel>
  );
}

/** How many shops have 0, 1, 2 ... active users: a horizontal histogram. */
export function ShopSizePanel({ data, loading }: Props) {
  const rows = data?.shopSizes ?? [];
  return (
    <Panel title="Shop size" subtitle="Shops by number of active users">
      {loading ? (
        <Loading />
      ) : (
        <Box sx={{ mx: -1 }} data-testid="size-chart">
          <BarChart
            height={240}
            layout="horizontal"
            margin={{ left: 0, right: 16, top: 8, bottom: 0 }}
            grid={{ vertical: true }}
            borderRadius={4}
            hideLegend
            yAxis={[
              {
                ...bandX(
                  rows.map((r) => r.label),
                  (v) => (v === '1' ? '1 user' : `${v} users`),
                ),
                width: 64,
              },
            ]}
            xAxis={[{ ...yCount, width: undefined, height: 24 }]}
            series={[
              {
                id: 'shops',
                label: 'Shops',
                data: rows.map((r) => r.shops),
                color: 'var(--mui-palette-primary-main)',
                valueFormatter: (v: number | null) => plural(v ?? 0, 'shop'),
              },
            ]}
            sx={chartSx}
          />
        </Box>
      )}
    </Panel>
  );
}
